import { Call } from '@/types/call';
import { UnitAnalyticsSummary } from '@/types/unit-analytics-summary';
import { Response } from '@/types/wellbeing-response';

type DateRange = {
  start: string | undefined;
  end: string | undefined;
};

export function computeUnitAnalytics(
  unitId: number,
  calls: Call[],
  responses: Response[],
  dateRange: DateRange,
): UnitAnalyticsSummary {
  const startDate = dateRange.start ? new Date(dateRange.start) : null;
  const endDate = dateRange.end
    ? new Date(dateRange.end + 'T23:59:59.999')
    : null;

  const filteredCalls = calls.filter((call) => {
    const placedAt = new Date(call.placedAt);
    if (startDate && placedAt < startDate) return false;
    if (endDate && placedAt > endDate) return false;
    return true;
  });

  const filteredResponses = responses.filter((resp) => {
    const sendTime = new Date(resp.sendTimeStamp);
    if (startDate && sendTime < startDate) return false;
    if (endDate && sendTime > endDate) return false;
    return true;
  });

  const missedCalls = filteredCalls.filter(
    (c) => c.callStatus === 'placed',
  ).length;

  const answeredCalls = filteredCalls.filter(
    (c) => c.callStatus === 'opened' || c.callStatus === 'terminated',
  ).length;

  const callsEndedByStaff = filteredCalls.filter(
    (c) => c.endedBy === 'dmp',
  ).length;

  const callsEndedByUser = filteredCalls.filter(
    (c) => c.endedBy === 'device',
  ).length;

  const maxCallDurationMinutes = filteredCalls.reduce((max, call) => {
    const placed = new Date(call.placedAt).getTime();
    const ended = new Date(call.endedAt).getTime();
    const durationMinutes = (ended - placed) / (1000 * 60);
    return Math.max(max, durationMinutes);
  }, 0);

  const notificationsSent = filteredResponses.filter(
    (r) => r.type === 'responseNotification',
  ).length;

  const announcementsSent = filteredResponses.filter(
    (r) => r.type === 'announcement',
  ).length;

  const notificationResponses = filteredResponses.filter(
    (r) => r.type === 'responseNotification' && r.response !== 'NoResponse',
  ).length;

  return {
    unitId,
    totalCalls: filteredCalls.length,
    missedCalls,
    answeredCalls,
    callsEndedByStaff,
    callsEndedByUser,
    maxCallDurationMinutes,
    notificationsSent,
    announcementsSent,
    notificationResponses,
  };
}
